import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import StudentEvents from '@/pages/StudentEvents';
import api from '@/lib/api';
import { apiErrorToast } from '@/lib/apiErrorToast';
import html2canvas from 'html2canvas';
import { getEventBackground, getQRDataUrl, parseColorTheme, pickPosterLayout } from '@/lib/posterUtils';
import { resolvePhotoUrl } from '@/lib/photo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Plus, CalendarDays, MapPin, Users, IndianRupee, Sparkles, QrCode,
  ExternalLink, Trash2, Edit, MoreHorizontal, ScanLine, Ticket, Share2, Palette, Layout,
  Wand2, Image as ImageIcon, Zap, Loader2, AlertCircle
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const eventTypes = ['Tournament', 'Camp', 'Trial', 'Workshop', 'Awareness', 'Exhibition'];

export default function Events() {
  const { user } = useAuth();
  // Phase 78 — auto-route students/parents to StudentEvents at the same URL.
  // Done at the top so hook order remains consistent (StudentEvents has its own hooks).
  if (user?.role === 'student' || user?.role === 'parent') {
    return <StudentEvents />;
  }
  return <OwnerEvents />;
}

function OwnerEvents() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPosterDialog, setShowPosterDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [posterPrompt, setPosterPrompt] = useState('');

  // Phase 274b — Soft pre-fill helper. Defaults the Poster dialog textarea to
  // a one-liner that already includes the event's description (tournament
  // rules, categories, age brackets). The user can still wipe it or append
  // style hints like "neon lighting, athletic poster style". Even though the
  // backend pulls description independently (Phase 274), pre-filling makes
  // the awareness visible inside the modal — eliminates copy-paste friction.
  const buildPosterDefaultPrompt = (event) => {
    if (!event) return '';
    const base = `Create a poster for ${event.title} at ${event.venue || 'Academy Ground'} on ${event.date}`;
    const desc = (event.description || '').trim();
    if (!desc) return base;
    return `${base}. Include event details: ${desc}`;
  };
  const [generatingPoster, setGeneratingPoster] = useState(false);
  const [posterResult, setPosterResult] = useState(null);
  // Phase 205-H — Decoupled Premium AI poster engine (Nano Banana + Object Storage)
  const [posterMode, setPosterMode] = useState('draft'); // 'draft' | 'premium'
  const [aiJobId, setAiJobId] = useState(null);
  const [aiJobStatus, setAiJobStatus] = useState(null); // null | 'pending' | 'processing' | 'completed' | 'failed'
  const [aiResultUrl, setAiResultUrl] = useState(null);
  const [aiErrorLog, setAiErrorLog] = useState(null);
  const aiPollRef = useRef(null);
  const [showViewPoster, setShowViewPoster] = useState(false);
  const [viewingPoster, setViewingPoster] = useState(null);
  const [showParticipants, setShowParticipants] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [academy, setAcademy] = useState(null);
  const [form, setForm] = useState({
    title: '', description: '', event_type: 'Tournament', date: '', end_date: '',
    venue: '', max_participants: '', entry_fee: '0', poster_prompt: ''
  });
  // Phase 274 — Edit Event flow. When set, the Create dialog flips into Edit mode
  // and submission goes to PUT /events/{id} instead of POST /events.
  const [editingEventId, setEditingEventId] = useState(null);

  const fetchEvents = useCallback(async () => {
    try { const res = await api.get('/events'); setEvents(res.data); }
    catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchEvents(); api.get('/academy').then(r => setAcademy(r.data)).catch(() => {}); }, [fetchEvents]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      // Phase 274 — same form is reused for Create + Edit. When editingEventId
      // is set, dispatch a PUT instead of a POST and patch the local list.
      const payload = {
        ...form,
        max_participants: form.max_participants ? parseInt(form.max_participants) : null,
        entry_fee: parseFloat(form.entry_fee) || 0
      };
      if (editingEventId) {
        const res = await api.put(`/events/${editingEventId}`, payload);
        setEvents(prev => prev.map(e => e.id === editingEventId ? res.data : e));
        toast.success('Event updated');
      } else {
        await api.post('/events', payload);
        toast.success('Event created');
        fetchEvents();
      }
      setShowCreateDialog(false);
      setEditingEventId(null);
      setForm({ title: '', description: '', event_type: 'Tournament', date: '', end_date: '', venue: '', max_participants: '', entry_fee: '0', poster_prompt: '' });
    } catch (err) {
      apiErrorToast(err, editingEventId ? 'Failed to update event' : 'Failed to create event');
    }
  };

  // Phase 274 — open the same Create dialog pre-populated for editing.
  const openEditDialog = (event) => {
    setEditingEventId(event.id);
    setForm({
      title: event.title || '',
      description: event.description || '',
      event_type: event.event_type || 'Tournament',
      date: (event.date || '').slice(0, 10),
      end_date: (event.end_date || '').slice(0, 10),
      venue: event.venue || '',
      max_participants: event.max_participants ? String(event.max_participants) : '',
      entry_fee: event.entry_fee != null ? String(event.entry_fee) : '0',
      poster_prompt: event.poster_prompt || '',
    });
    setShowCreateDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try { await api.delete(`/events/${id}`); toast.success('Deleted'); fetchEvents(); }
    catch (err) { apiErrorToast(err, 'Failed to delete event'); }
  };

  const [rsvpStats, setRsvpStats] = useState(null);

  const handleViewParticipants = async (event) => {
    setSelectedEvent(event);
    try {
      const res = await api.get(`/events/${event.id}/registrations`);
      setParticipants(res.data || []);
    } catch (err) { setParticipants([]); }
    try {
      const r2 = await api.get(`/events/${event.id}/rsvps`);
      setRsvpStats(r2.data || null);
    } catch { setRsvpStats(null); }
    setShowParticipants(true);
  };

  const handleGeneratePoster = async () => {
    if (!selectedEvent || !posterPrompt) return;
    setGeneratingPoster(true);
    setPosterResult(null);
    try {
      const res = await api.post('/ai/generate-poster', { event_id: selectedEvent.id, prompt: posterPrompt });
      setPosterResult(res.data.poster_data || res.data);
      if (res.data.ai_powered) {
        toast.success('AI poster generated successfully!');
      } else {
        toast.success('Poster generated (fallback mode)');
      }
      fetchEvents();
    } catch (err) { apiErrorToast(err, 'Failed to generate poster'); }
    finally { setGeneratingPoster(false); }
  };

  // ── Phase 205-H ── Premium AI Poster (Nano Banana → Object Storage → polling)
  const stopAiPolling = useCallback(() => {
    if (aiPollRef.current) {
      clearInterval(aiPollRef.current);
      aiPollRef.current = null;
    }
  }, []);

  const resetAiPosterState = useCallback(() => {
    stopAiPolling();
    setAiJobId(null);
    setAiJobStatus(null);
    setAiResultUrl(null);
    setAiErrorLog(null);
  }, [stopAiPolling]);

  const handleGeneratePremiumAI = async () => {
    if (!selectedEvent) return;
    setGeneratingPoster(true);
    resetAiPosterState();
    try {
      // 1) Get the text design (Quick Draft) so headline/CTA/QR render immediately
      if (!posterResult) {
        try {
          const draftRes = await api.post('/ai/generate-poster', { event_id: selectedEvent.id, prompt: posterPrompt || `Create a poster for ${selectedEvent.title}` });
          setPosterResult(draftRes.data.poster_data || draftRes.data);
        } catch (e) { /* draft fallback is non-blocking — image can still render */ }
      }
      // 2) Kick off async image job
      const res = await api.post('/ai/generate-poster-image', {
        event_id: selectedEvent.id,
        sport: selectedEvent.event_type || selectedEvent.sport || selectedEvent.title,
        prompt: posterPrompt || '',
      });
      const { job_id } = res.data;
      setAiJobId(job_id);
      setAiJobStatus('pending');

      // 3) Poll every 3s until completed/failed (max 60s)
      let polls = 0;
      aiPollRef.current = setInterval(async () => {
        polls += 1;
        try {
          const poll = await api.get(`/ai/poster-job/${job_id}`);
          const { status, result_url, error_log } = poll.data;
          setAiJobStatus(status);
          if (status === 'completed' && result_url) {
            setAiResultUrl(result_url);
            stopAiPolling();
            setGeneratingPoster(false);
            toast.success('Premium AI poster ready!');
            fetchEvents();
          } else if (status === 'failed') {
            setAiErrorLog(error_log || 'Generation failed');
            stopAiPolling();
            setGeneratingPoster(false);
            toast.error('Premium AI failed — try again or use Quick Draft');
          } else if (polls >= 20) {
            // 20 polls × 3s = 60s — give up
            setAiErrorLog('Timed out waiting for AI generation (60s)');
            setAiJobStatus('failed');
            stopAiPolling();
            setGeneratingPoster(false);
            toast.error('AI generation timed out — please retry');
          }
        } catch (e) {
          // Transient error during poll — keep trying
          if (polls >= 20) {
            stopAiPolling();
            setGeneratingPoster(false);
            setAiJobStatus('failed');
            setAiErrorLog('Polling failed');
          }
        }
      }, 3000);
    } catch (err) {
      stopAiPolling();
      setGeneratingPoster(false);
      apiErrorToast(err, 'Failed to start Premium AI generation');
    }
  };

  // Cleanup poll interval on unmount or modal close
  useEffect(() => () => stopAiPolling(), [stopAiPolling]);

  const getStatusColor = (status) => {
    switch(status) {
      case 'upcoming': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'ongoing': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
      case 'completed': return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400';
      case 'cancelled': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  if (loading) return (
    <div className="space-y-4" data-testid="events-loading">
      <Skeleton className="h-8 w-48" />
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-48 rounded-xl" />)}</div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="events-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-semibold">Events</h1>
          <p className="text-sm text-muted-foreground">{events.length} events</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={(open) => {
          setShowCreateDialog(open);
          if (!open) {
            // Phase 274 — clear edit mode when dialog closes (via X or backdrop click)
            setEditingEventId(null);
            setForm({ title: '', description: '', event_type: 'Tournament', date: '', end_date: '', venue: '', max_participants: '', entry_fee: '0', poster_prompt: '' });
          }
        }}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="events-create-button"><Plus className="h-4 w-4 mr-1" /> Create Event</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={editingEventId ? 'event-edit-dialog' : 'event-create-dialog'}>
            <DialogHeader>
              <DialogTitle>{editingEventId ? 'Edit Event' : 'Create Event'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              <div className="space-y-1.5"><Label className="text-xs">Title *</Label><Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Annual Football Tournament" required data-testid="event-title-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Event details..." rows={3} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Type</Label>
                  <Select value={form.event_type} onValueChange={(v) => setForm({...form, event_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{eventTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label className="text-xs">Venue</Label><Input value={form.venue} onChange={(e) => setForm({...form, venue: e.target.value})} placeholder="Stadium, City" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">Start Date *</Label><Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} required /></div>
                <div className="space-y-1.5"><Label className="text-xs">End Date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({...form, end_date: e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">Max Participants</Label><Input type="number" value={form.max_participants} onChange={(e) => setForm({...form, max_participants: e.target.value})} placeholder="100" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Entry Fee (₹)</Label><Input type="number" value={form.entry_fee} onChange={(e) => setForm({...form, entry_fee: e.target.value})} placeholder="0" /></div>
              </div>
              <Button type="submit" className="w-full" data-testid={editingEventId ? 'event-update-submit' : 'event-create-submit'}>
                {editingEventId ? 'Save Changes' : 'Create Event'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Events Grid */}
      {events.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <CalendarDays className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold mb-1">No events yet</h3>
            <p className="text-sm text-muted-foreground mb-4">Create your first event to get started</p>
            <Button size="sm" onClick={() => setShowCreateDialog(true)}>Create Event</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {events.map((event) => (
            <Card key={event.id} className="group hover:shadow-md hover:-translate-y-px transition-[box-shadow,transform] duration-200" data-testid={`event-card-${event.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mb-2 ${getStatusColor(event.status)}`}>{event.status}</span>
                    <h3 className="font-semibold text-base">{event.title}</h3>
                    {event.custom_id && <span className="text-xs font-mono text-muted-foreground">{event.custom_id}</span>}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => openEditDialog(event)}
                        data-testid={`event-edit-${event.id}`}
                      >
                        <Edit className="h-4 w-4 mr-2" /> Edit Event
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => { setSelectedEvent(event); setPosterPrompt(buildPosterDefaultPrompt(event)); setShowPosterDialog(true); }}
                        data-testid={`event-poster-${event.id}`}
                      >
                        <Sparkles className="h-4 w-4 mr-2" /> AI Poster
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDelete(event.id)}
                        data-testid={`event-delete-${event.id}`}
                        className="text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                {event.description && <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{event.description}</p>}
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <CalendarDays className="h-3.5 w-3.5" />
                    <span>{event.date}{event.end_date && event.end_date !== event.date ? ` — ${event.end_date}` : ''}</span>
                  </div>
                  {event.venue && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="h-3.5 w-3.5" /><span>{event.venue}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Users className="h-3.5 w-3.5" />
                      <span>{event.participant_count}{event.max_participants ? `/${event.max_participants}` : ''}</span>
                    </div>
                    {event.entry_fee > 0 && (
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <IndianRupee className="h-3.5 w-3.5" /><span>{event.entry_fee}</span>
                      </div>
                    )}
                  </div>
                </div>
                {/* Revenue stats */}
                {event.entry_fee > 0 && event.participant_count > 0 && (
                  <div className="mt-2 p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
                    <p className="text-xs font-semibold text-emerald-700 tabular-nums">₹{(event.entry_fee * event.participant_count).toLocaleString()} collected</p>
                  </div>
                )}
                {/* 3 Action Buttons */}
                <div className="mt-3 pt-3 border-t border-border flex items-center gap-2 flex-wrap">
                  <Button size="sm" variant="outline" className="h-7 text-xs flex-1 min-w-[100px]" onClick={() => handleViewParticipants(event)} data-testid={`view-participants-${event.id}`}>
                    <Users className="h-3 w-3 mr-1" /> {event.participant_count || 0} Enrolled
                  </Button>
                  {/* Phase 87 — Coach-side check-in scanner launcher */}
                  <Button size="sm" variant="outline" className="h-7 text-xs border-emerald-200 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 text-emerald-700" onClick={() => navigate(`/events/${event.id}/check-in`)} data-testid={`checkin-launch-${event.id}`}>
                    <ScanLine className="h-3 w-3 mr-1" /> Check-In
                  </Button>
                  {event.poster_data ? (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setSelectedEvent(event); setViewingPoster(event.poster_data); setShowViewPoster(true); }}>
                      <Sparkles className="h-3 w-3 mr-1" /> Poster
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setSelectedEvent(event); setPosterPrompt(buildPosterDefaultPrompt(event)); setShowPosterDialog(true); }}>
                      <Sparkles className="h-3 w-3 mr-1" /> AI Poster
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* AI Poster Dialog */}
      <Dialog open={showPosterDialog} onOpenChange={(open) => { setShowPosterDialog(open); if (!open) { setPosterResult(null); resetAiPosterState(); setPosterMode('draft'); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> AI Poster Generator</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Phase 205-H — Dual-mode selector */}
            <div className="grid grid-cols-2 gap-2" data-testid="poster-mode-selector">
              <button
                type="button"
                onClick={() => setPosterMode('draft')}
                className={`p-3 rounded-xl border-2 text-left transition-all ${posterMode === 'draft' ? 'border-teal-500 bg-teal-50 dark:bg-teal-950/30 shadow-sm' : 'border-border bg-card hover:border-teal-300'}`}
                data-testid="poster-mode-draft"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Zap className={`h-4 w-4 ${posterMode === 'draft' ? 'text-teal-600' : 'text-muted-foreground'}`} />
                  <span className="font-semibold text-sm">Quick Draft</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-bold">~3s</span>
                </div>
                <p className="text-[11px] text-muted-foreground leading-snug">CSS-styled gradient with stock background. Instant preview.</p>
              </button>
              <button
                type="button"
                onClick={() => setPosterMode('premium')}
                className={`p-3 rounded-xl border-2 text-left transition-all ${posterMode === 'premium' ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/30 shadow-sm' : 'border-border bg-card hover:border-amber-300'}`}
                data-testid="poster-mode-premium"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Wand2 className={`h-4 w-4 ${posterMode === 'premium' ? 'text-amber-600' : 'text-muted-foreground'}`} />
                  <span className="font-semibold text-sm">Premium AI</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-bold">~15s</span>
                </div>
                <p className="text-[11px] text-muted-foreground leading-snug">Photoreal Nano Banana sports action backdrop.</p>
              </button>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Poster Prompt {posterMode === 'premium' && <span className="text-amber-700">(style hint for AI image)</span>}</Label>
              <Textarea value={posterPrompt} onChange={(e) => setPosterPrompt(e.target.value)} rows={3} placeholder={posterMode === 'premium' ? 'e.g. sunset orange palette, dramatic shadows...' : 'Describe your event poster...'} data-testid="poster-prompt-input" />
              {selectedEvent?.description && posterPrompt.includes(selectedEvent.description) && (
                <p className="text-[10px] text-muted-foreground flex items-start gap-1" data-testid="poster-prompt-desc-helper">
                  <Sparkles className="h-3 w-3 mt-px text-amber-500 flex-shrink-0" />
                  <span>Event description auto-filled. Add style hints (e.g. &ldquo;neon lighting, athletic poster style&rdquo;) or wipe and retype.</span>
                </p>
              )}
            </div>

            {posterMode === 'draft' ? (
              <Button onClick={handleGeneratePoster} disabled={generatingPoster || !posterPrompt} className="w-full" data-testid="poster-generate-button">
                {generatingPoster ? 'Generating...' : posterResult ? 'Regenerate Quick Draft' : 'Generate Quick Draft'}
              </Button>
            ) : (
              <Button onClick={handleGeneratePremiumAI} disabled={generatingPoster} className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700" data-testid="poster-generate-premium-button">
                {generatingPoster ? (
                  <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> {aiJobStatus === 'processing' ? 'Generating image...' : 'Starting AI…'}</span>
                ) : aiResultUrl ? 'Regenerate Premium AI Poster' : 'Generate Premium AI Poster'}
              </Button>
            )}

            {/* Premium AI status panel */}
            {posterMode === 'premium' && (aiJobStatus || aiErrorLog) && (
              <div className={`rounded-lg border p-3 text-xs ${aiJobStatus === 'failed' ? 'border-red-200 bg-red-50 dark:bg-red-950/20' : aiJobStatus === 'completed' ? 'border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20' : 'border-amber-200 bg-amber-50 dark:bg-amber-950/20'}`} data-testid="poster-ai-status-panel">
                {aiJobStatus === 'failed' ? (
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-red-600 flex-none mt-0.5" />
                      <div className="flex-1">
                        <p className="font-semibold text-red-700">Premium AI generation failed</p>
                        {aiErrorLog && <p className="text-red-600 mt-0.5 break-all">{aiErrorLog}</p>}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handleGeneratePremiumAI} data-testid="poster-ai-retry-btn">Retry</Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setPosterMode('draft'); resetAiPosterState(); }} data-testid="poster-ai-fallback-draft-btn">Use Quick Draft Instead</Button>
                    </div>
                  </div>
                ) : aiJobStatus === 'completed' ? (
                  <div className="flex items-center gap-2 text-emerald-700">
                    <ImageIcon className="h-4 w-4" />
                    <span className="font-semibold">Premium AI backdrop ready</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-amber-700">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="font-semibold">{aiJobStatus === 'processing' ? 'Nano Banana is painting your scene…' : 'Queued…'}</span>
                  </div>
                )}
              </div>
            )}

            {/* Poster Preview */}
            {posterResult && (() => {
              // Phase 202.c.2 — translate AI hints into actual rendering.
              const theme = parseColorTheme(posterResult.color_theme);
              const layout = pickPosterLayout(posterResult.layout_description);
              // Phase 205-H — swap stock background with AI-generated backdrop when ready
              const bgSrc = (posterMode === 'premium' && aiResultUrl) ? resolvePhotoUrl(aiResultUrl) : getEventBackground(selectedEvent?.event_type, selectedEvent?.title);
              const bgOpacityClass = (posterMode === 'premium' && aiResultUrl) ? 'opacity-80' : 'opacity-30 mix-blend-overlay';
              const downloadPNG = async () => {
                const el = document.getElementById('poster-content');
                if (!el) return;
                try {
                  const canvas = await html2canvas(el, { backgroundColor: theme.from, scale: 2, useCORS: true, allowTaint: true });
                  const link = document.createElement('a');
                  link.download = `${selectedEvent?.title?.replace(/\s+/g, '_') || 'poster'}.png`;
                  link.href = canvas.toDataURL('image/png');
                  link.click();
                  toast.success('Poster downloaded!');
                } catch (e) { toast.error('Download failed'); }
              };
              const shareWhatsApp = async () => {
                const text = encodeURIComponent(`🏆 ${posterResult.headline}\n${posterResult.subheadline || ''}\n${posterResult.date_display ? '📅 ' + posterResult.date_display : ''}${posterResult.venue_display ? '\n📍 ' + posterResult.venue_display : ''}\n${posterResult.cta_text || 'Register Now!'}\n\nMore: ${selectedEvent?.qr_code || 'https://out-play.in'}`);
                window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener');
                toast.success('WhatsApp opened — download the PNG first if you want to attach the visual');
              };
              return (
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                  {/* AI signal badges so the user can see what the model chose */}
                  <div className="flex items-center gap-1.5 px-3 py-2 bg-slate-900/95 text-white text-[10px]">
                    <Palette className="h-3 w-3 text-amber-300" />
                    <span className="font-semibold uppercase tracking-wide">Theme:</span>
                    <span className="text-white/80 italic" data-testid="poster-theme-label">{posterResult.color_theme || 'default'}</span>
                    <span className="mx-1.5 text-white/30">·</span>
                    <Layout className="h-3 w-3 text-teal-300" />
                    <span className="font-semibold uppercase tracking-wide">Layout:</span>
                    <span className="text-white/80" data-testid="poster-layout-label">{layout.replace('-', ' ')}</span>
                    {posterMode === 'premium' && aiResultUrl && (
                      <>
                        <span className="mx-1.5 text-white/30">·</span>
                        <Wand2 className="h-3 w-3 text-amber-300" />
                        <span className="font-extrabold uppercase tracking-wide text-amber-300" data-testid="poster-premium-badge">Premium AI</span>
                      </>
                    )}
                  </div>
                  <div
                    id="poster-content"
                    className="relative overflow-hidden"
                    style={{
                      minHeight: '500px',
                      color: theme.text,
                      background: `linear-gradient(135deg, ${theme.from} 0%, ${theme.to} 100%)`,
                    }}
                    data-testid="poster-preview-card"
                  >
                    <img src={bgSrc} alt="" className={`absolute inset-0 w-full h-full object-cover ${bgOpacityClass}`} crossOrigin="anonymous" data-testid="poster-bg-img" />
                    {/* Phase 205-H — softer gradient overlay when AI image is present so the photo shows through */}
                    {posterMode === 'premium' && aiResultUrl ? (
                      <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${theme.from}99 0%, ${theme.from}55 50%, ${theme.to}cc 100%)` }} />
                    ) : (
                      <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${theme.from}cc 0%, ${theme.from}55 50%, ${theme.to}ee 100%)` }} />
                    )}
                    <div className="relative z-10 p-6 space-y-4 flex flex-col min-h-[500px]">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {academy?.logo_url ? <img src={academy.logo_url} alt="" className="h-9 w-9 rounded-lg object-contain bg-white/20 p-0.5" /> : <div className="h-9 w-9 rounded-lg bg-white/20 flex items-center justify-center font-bold text-xs">{(academy?.name || 'A')[0]}</div>}
                          <div><p className="text-sm font-bold">{academy?.name || 'Academy'}</p><p className="text-[10px] uppercase tracking-widest" style={{ color: theme.accent }}>{selectedEvent?.event_type}</p></div>
                        </div>
                        <span className="text-[10px] px-2.5 py-1 rounded-full font-extrabold uppercase" style={{ backgroundColor: theme.accent, color: '#0f172a' }}>Register Now</span>
                      </div>

                      {layout === 'split-left' ? (
                        <div className="flex-1 grid grid-cols-5 gap-4 items-center py-2">
                          <div className="col-span-3 text-left space-y-3">
                            <h1 className="text-3xl font-display font-extrabold leading-tight drop-shadow-lg">{posterResult.headline}</h1>
                            {posterResult.subheadline && <p className="text-base text-white/85">{posterResult.subheadline}</p>}
                            {posterResult.cta_text && <div className="pt-2"><span className="inline-block px-7 py-2.5 rounded-2xl text-sm font-extrabold shadow-xl" style={{ background: `linear-gradient(135deg, ${theme.accent}, ${theme.to})`, color: '#0f172a' }}>{posterResult.cta_text}</span></div>}
                          </div>
                          <div className="col-span-2 space-y-1.5">
                            {posterResult.date_display && <div className="flex items-center gap-1.5 bg-white/15 backdrop-blur px-3 py-1.5 rounded-lg border border-white/10 text-xs"><CalendarDays className="h-3.5 w-3.5" style={{ color: theme.accent }} /><span>{posterResult.date_display}</span></div>}
                            {posterResult.venue_display && <div className="flex items-center gap-1.5 bg-white/15 backdrop-blur px-3 py-1.5 rounded-lg border border-white/10 text-xs"><MapPin className="h-3.5 w-3.5" style={{ color: theme.accent }} /><span>{posterResult.venue_display}</span></div>}
                            {posterResult.key_details && posterResult.key_details.slice(0, 3).map((d, i) => <div key={i} className="text-[11px] bg-white/10 backdrop-blur px-3 py-1.5 rounded-lg text-white/90">• {d}</div>)}
                          </div>
                        </div>
                      ) : layout === 'minimal-stack' ? (
                        <div className="flex-1 flex flex-col justify-center text-left space-y-3 py-4 pl-2">
                          <div className="h-1 w-16 rounded-full" style={{ backgroundColor: theme.accent }} />
                          <h1 className="text-3xl font-display font-extrabold leading-tight drop-shadow-lg">{posterResult.headline}</h1>
                          {posterResult.subheadline && <p className="text-base text-white/80 max-w-md">{posterResult.subheadline}</p>}
                          <div className="flex flex-wrap gap-2 pt-2 text-xs">
                            {posterResult.date_display && <span className="bg-white/10 backdrop-blur px-3 py-1.5 rounded-md">📅 {posterResult.date_display}</span>}
                            {posterResult.venue_display && <span className="bg-white/10 backdrop-blur px-3 py-1.5 rounded-md">📍 {posterResult.venue_display}</span>}
                          </div>
                          {posterResult.cta_text && <div className="pt-3"><span className="inline-block px-7 py-2.5 rounded-md text-sm font-bold tracking-wide" style={{ backgroundColor: theme.accent, color: '#0f172a' }}>{posterResult.cta_text} →</span></div>}
                        </div>
                      ) : (
                        // hero-center (default, matches original layout)
                        <div className="flex-1 flex flex-col justify-center text-center space-y-3 py-4">
                          <h1 className="text-3xl font-display font-extrabold leading-tight drop-shadow-lg">{posterResult.headline}</h1>
                          {posterResult.subheadline && <p className="text-base text-white/80 max-w-sm mx-auto">{posterResult.subheadline}</p>}
                          <div className="flex justify-center gap-3 text-sm pt-2">
                            {posterResult.date_display && <div className="flex items-center gap-1.5 bg-white/15 backdrop-blur px-3 py-1.5 rounded-lg border border-white/10"><CalendarDays className="h-3.5 w-3.5" style={{ color: theme.accent }} /><span>{posterResult.date_display}</span></div>}
                            {posterResult.venue_display && <div className="flex items-center gap-1.5 bg-white/15 backdrop-blur px-3 py-1.5 rounded-lg border border-white/10"><MapPin className="h-3.5 w-3.5" style={{ color: theme.accent }} /><span>{posterResult.venue_display}</span></div>}
                          </div>
                          {posterResult.key_details && <div className="grid grid-cols-2 gap-1.5 max-w-sm mx-auto pt-2">{posterResult.key_details.map((d, i) => <div key={i} className="text-xs bg-white/10 backdrop-blur px-3 py-1.5 rounded-lg text-white/90 text-left">• {d}</div>)}</div>}
                          {posterResult.cta_text && <div className="pt-3"><span className="inline-block px-8 py-3 rounded-2xl text-sm font-extrabold shadow-xl" style={{ background: `linear-gradient(135deg, ${theme.accent}, ${theme.to})`, color: '#0f172a' }}>{posterResult.cta_text}</span></div>}
                        </div>
                      )}

                      <div className="flex items-end justify-between pt-3 border-t border-white/20">
                        <div className="space-y-0.5"><p className="text-sm font-bold">{academy?.name || ''}</p>{academy?.phone && <p className="text-[10px] text-white/70">📞 {academy.phone}</p>}{academy?.city && <p className="text-[10px] text-white/70">📍 {academy.city}</p>}<p className="text-[10px] text-white/70">🌐 out-play.in</p></div>
                        <div className="text-center bg-white rounded-xl p-1.5"><img src={getQRDataUrl(selectedEvent?.qr_code || 'https://out-play.in', 80)} alt="QR" className="h-16 w-16" crossOrigin="anonymous" /><p className="text-[7px] text-black font-bold mt-0.5">SCAN TO ENROLL</p></div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 p-3 bg-card border-t">
                    <Button size="sm" variant="outline" className="flex-1 min-w-[120px]" onClick={() => { setShowPosterDialog(false); setPosterResult(null); fetchEvents(); }} data-testid="poster-use-btn">Use This Poster</Button>
                    <Button size="sm" variant="outline" onClick={handleGeneratePoster} disabled={generatingPoster} data-testid="poster-regenerate-btn">Regenerate</Button>
                    <Button size="sm" variant="outline" onClick={shareWhatsApp} data-testid="poster-share-whatsapp-btn"><Share2 className="h-3.5 w-3.5 mr-1" /> WhatsApp</Button>
                    <Button size="sm" onClick={downloadPNG} data-testid="poster-download-btn">Download PNG</Button>
                  </div>
                </div>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Participants Dialog */}
      <Dialog open={showParticipants} onOpenChange={setShowParticipants}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              {selectedEvent?.title} — Participants
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-center">
                <p className="text-lg font-display font-bold text-emerald-700 tabular-nums">{selectedEvent?.participant_count || 0}{selectedEvent?.max_participants ? `/${selectedEvent.max_participants}` : ''}</p>
                <p className="text-[10px] text-emerald-600">Enrolled</p>
              </div>
              <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-center">
                <p className="text-lg font-display font-bold text-blue-700 tabular-nums">₹{((selectedEvent?.entry_fee || 0) * (selectedEvent?.participant_count || 0)).toLocaleString()}</p>
                <p className="text-[10px] text-blue-600">Revenue</p>
              </div>
              <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-center">
                <p className="text-lg font-display font-bold text-amber-700 tabular-nums">₹{selectedEvent?.entry_fee || 0}</p>
                <p className="text-[10px] text-amber-600">Entry Fee</p>
              </div>
            </div>
            {/* Phase 78 — RSVP summary */}
            {rsvpStats && rsvpStats.total > 0 && (
              <div className="rounded-lg border bg-muted/30 p-3" data-testid="event-rsvp-summary">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-2">RSVP Responses ({rsvpStats.total})</p>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <div className="text-center p-1.5 rounded bg-emerald-50 dark:bg-emerald-900/20">
                    <p className="text-base font-bold text-emerald-700 tabular-nums">{rsvpStats.counts.attending}</p>
                    <p className="text-[10px] text-emerald-600">Attending</p>
                  </div>
                  <div className="text-center p-1.5 rounded bg-amber-50 dark:bg-amber-900/20">
                    <p className="text-base font-bold text-amber-700 tabular-nums">{rsvpStats.counts.maybe}</p>
                    <p className="text-[10px] text-amber-600">Maybe</p>
                  </div>
                  <div className="text-center p-1.5 rounded bg-slate-100 dark:bg-slate-800">
                    <p className="text-base font-bold text-slate-700 tabular-nums">{rsvpStats.counts.not_attending}</p>
                    <p className="text-[10px] text-slate-600">Can&apos;t make it</p>
                  </div>
                </div>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {rsvpStats.rsvps.map(r => (
                    <div key={r.id} className="flex items-center justify-between text-xs px-2 py-1 rounded hover:bg-background">
                      <span className="truncate">{r.student_name}</span>
                      <Badge variant="outline" className={`text-[9px] ${
                        r.status === 'attending' ? 'border-emerald-300 text-emerald-700 bg-emerald-50' :
                        r.status === 'maybe' ? 'border-amber-300 text-amber-700 bg-amber-50' :
                        'border-slate-300 text-slate-600'
                      }`}>{r.status === 'not_attending' ? "Can't" : r.status}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <Table>
              <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Contact</TableHead><TableHead className="whitespace-nowrap">Team</TableHead><TableHead>Payment</TableHead><TableHead>Registered</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {participants.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No participants yet. Share the event QR code to get enrollments.</TableCell></TableRow>
                ) : participants.map((p, i) => {
                  const ps = p.payment_status || 'free';
                  const psLabel = ps === 'paid' ? 'Paid' : ps === 'free' ? 'Free' : ps === 'pending' ? 'Pending' : ps;
                  const psClass = ps === 'paid' ? 'bg-emerald-100 text-emerald-700' : ps === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700';
                  return (
                  <TableRow key={i} data-testid={`participant-row-${i}`}>
                    <TableCell className="font-medium text-sm">{p.participant_name}</TableCell>
                    <TableCell className="text-xs"><div>{p.participant_email}</div><div className="text-muted-foreground">{p.participant_phone}</div></TableCell>
                    <TableCell className="whitespace-nowrap text-sm">{p.team_name || '-'}</TableCell>
                    <TableCell>
                      <Badge className={`text-[10px] border-0 ${psClass}`} data-testid={`participant-payment-${i}`}>
                        {ps === 'paid' && p.paid_amount ? `₹${p.paid_amount} ${psLabel}` : psLabel}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{new Date(p.registered_at).toLocaleDateString('en-IN')}</TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Print gate pass" onClick={() => window.open(`/events/${selectedEvent.id}/registrations/${p.id}/gatepass`, '_blank')} data-testid={`participant-gatepass-${i}`}>
                        <Ticket className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );})}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Poster Dialog */}
      <Dialog open={showViewPoster} onOpenChange={setShowViewPoster}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="view-poster-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> AI Event Poster</DialogTitle>
          </DialogHeader>
          {viewingPoster ? (
            <div className="space-y-4">
              <div className="rounded-2xl overflow-hidden shadow-2xl" id="poster-view-content">
                <div className="relative text-white overflow-hidden" style={{ minHeight: '500px' }}>
                  <img src={getEventBackground(selectedEvent?.event_type, selectedEvent?.title)} alt="" className="absolute inset-0 w-full h-full object-cover" crossOrigin="anonymous" />
                  <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/90" />
                  <div className="relative z-10 space-y-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center"><Sparkles className="h-5 w-5 text-teal-300" /></div>
                        <div><span className="text-[10px] text-teal-300 font-medium uppercase tracking-widest block">{selectedEvent?.event_type || 'Event'}</span><span className="text-[10px] text-slate-400">{selectedEvent?.custom_id}</span></div>
                      </div>
                      <span className="text-xs bg-gradient-to-r from-amber-400 to-orange-400 text-slate-900 px-3 py-1 rounded-full font-bold">REGISTER NOW</span>
                    </div>
                    <div className="text-center py-2">
                      <h1 className="text-3xl font-display font-bold leading-tight">{viewingPoster.headline}</h1>
                      {viewingPoster.subheadline && <p className="text-base text-teal-200 mt-2 max-w-md mx-auto">{viewingPoster.subheadline}</p>}
                    </div>
                    <div className="flex justify-center gap-4 text-sm">
                      {viewingPoster.date_display && <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-xl"><CalendarDays className="h-4 w-4 text-teal-300" /><span>{viewingPoster.date_display}</span></div>}
                      {viewingPoster.venue_display && <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-xl"><MapPin className="h-4 w-4 text-teal-300" /><span>{viewingPoster.venue_display}</span></div>}
                    </div>
                    {viewingPoster.key_details && (
                      <div className="grid grid-cols-2 gap-2 max-w-md mx-auto">
                        {viewingPoster.key_details.map((d, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm bg-white/5 px-3 py-2.5 rounded-xl"><span className="h-2 w-2 rounded-full bg-gradient-to-r from-teal-400 to-emerald-400 flex-shrink-0" /><span className="text-slate-200">{d}</span></div>
                        ))}
                      </div>
                    )}
                    {viewingPoster.cta_text && (
                      <div className="text-center pt-3"><span className="inline-block bg-gradient-to-r from-teal-400 to-emerald-400 text-slate-900 px-8 py-3 rounded-2xl text-base font-bold shadow-xl shadow-teal-500/20">{viewingPoster.cta_text}</span></div>
                    )}
                    <div className="pt-4 border-t border-white/10 flex items-end justify-between">
                      <div className="space-y-0.5 text-[10px] text-slate-400">
                        <p className="text-white text-sm font-semibold">{academy?.name || selectedEvent?.venue || 'Academy'}</p>
                        {academy?.phone && <p>📞 {academy.phone}</p>}
                        {academy?.city && <p>📍 {academy.city}</p>}
                        <p>🌐 out-play.in</p>
                      </div>
                      <div className="text-center bg-white rounded-xl p-2">
                        <img src={getQRDataUrl(selectedEvent?.qr_code || 'https://out-play.in', 90)} alt="QR" className="h-20 w-20" crossOrigin="anonymous" />
                        <p className="text-[8px] text-black font-bold mt-0.5">SCAN TO ENROLL</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {selectedEvent && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 text-sm">
                  <div><span className="font-medium">{selectedEvent.title}</span><span className="text-muted-foreground ml-2">{selectedEvent.participant_count || 0}{selectedEvent.max_participants ? `/${selectedEvent.max_participants}` : ''} enrolled</span></div>
                  {selectedEvent.entry_fee > 0 && <Badge variant="outline">₹{selectedEvent.entry_fee} entry</Badge>}
                </div>
              )}
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => { setPosterPrompt(`Regenerate poster for ${selectedEvent?.title}`); setShowViewPoster(false); setShowPosterDialog(true); }}><Sparkles className="h-4 w-4 mr-2" /> Regenerate</Button>
                <Button variant="outline" onClick={async () => { const el = document.getElementById('poster-view-content'); if (el) { try { const canvas = await html2canvas(el, { backgroundColor: '#0f172a', scale: 2 }); const link = document.createElement('a'); link.download = `${selectedEvent?.title?.replace(/\s+/g, '_') || 'poster'}.png`; link.href = canvas.toDataURL('image/png'); link.click(); toast.success('Poster downloaded!'); } catch(e) { toast.error('Download failed'); } } }}>Download PNG</Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground space-y-3">
              <Sparkles className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" />
              <p className="text-sm">No poster data available.</p>
              <Button size="sm" onClick={() => { setShowViewPoster(false); setPosterPrompt(`Create a poster for ${selectedEvent?.title} at ${selectedEvent?.venue || 'Academy Ground'} on ${selectedEvent?.date}`); setShowPosterDialog(true); }}>
                <Sparkles className="h-4 w-4 mr-2" /> Generate AI Poster
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
